"use strict";
/**
 * Created by jonas on 2020-09-09.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameFlowController = void 0;
const GameMode_1 = require("./GameMode");
const APIEventSystem_1 = require("@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem");
const SlotGame_1 = require("../SlotGame");
const SlotStateHandler_1 = require("@nolimitcity/slot-launcher/bin/plugins/apiplugin/SlotStateHandler");
const SkippableFlowStateController_1 = require("./SkippableFlowStateController");
const Logger_1 = require("@nolimitcity/slot-launcher/bin/utils/Logger");
const FlowState_1 = require("./FlowState");
class GameFlowController {
    constructor(apiPlugin, model) {
        this._mode = GameMode_1.GameMode.NORMAL;
        this._prevMode = this._mode;
        this._minimumGameRoundTimeInMs = 0;
        this._normalRoundCount = 0;
        this._accumulatedRoundTime = 0;
        this._apiPlugin = apiPlugin;
        this._apiPlugin.events.on(APIEventSystem_1.APIEvent.STATE, (state) => this.onApiState(state));
        this._gameModeMap = new Map();
        this._model = model;
        this.flowStateLogger = Logger_1.Logger.createNamedLogger("FlowState");
        this.roundStatsLogger = Logger_1.Logger.createNamedLogger("Stats");
    }
    /**
     * Starts the Game Flow Controller. You need to call this in the launcher start phase.
     */
    start() {
        this._minimumGameRoundTimeInMs = SlotGame_1.SlotGame.apiPlugin.gameClientConfiguration.minimumSpinTime;
        this._mode = this._model.data.nextMode;
        this.startFlowStatePresentation(FlowState_1.FlowState.IDLE);
    }
    /**
     * Adds a presentation map, mapped to a gameMode.
     * @param gameMode
     * @param presentationMap
     */
    addGameModeFlowStatePresentationMap(gameMode, presentationMap) {
        this._gameModeMap.set(gameMode, presentationMap);
    }
    /**
     *  Calculates the required additional time needed to make RESULT presentation long enough for current Jurisdiction.
     *  Calculation is based on time of bet and current time.
     * @param stopDuration The length of your stop animation
     */
    calculatePaddingDuration(stopDuration) {
        const stopDurationInMs = stopDuration * 1000;
        const timeFromStartInMs = Date.now() - this._betStartDateTime;
        const padding = (this._minimumGameRoundTimeInMs - timeFromStartInMs - stopDurationInMs) / 1000;
        return Math.max(0, padding);
    }
    getStateMap(mode) {
        const stateMap = this._gameModeMap.get(mode) || this._gameModeMap.get(GameMode_1.GameMode.NORMAL);
        if (stateMap == undefined) {
            throw new Error("You need at least one Game Mode Sequence, for GameMode.NORMAL");
        }
        return stateMap;
    }
    startFlowStatePresentation(state) {
        if (this._currentPresentation != undefined) {
            throw new Error(`Current presentation is not complete for state: ${state} mode: ${this._prevMode}`);
        }
        this._stateMap = this.getStateMap(this._mode);
        this._prevMode = this._mode;
        this.measureTimeAtStateStart(this._mode, state);
        this.flowStateLogger.log(`${this._mode}, ${state} start`);
        const presentation = this._stateMap.get(state);
        if (presentation != undefined) {
            this._currentPresentation = presentation;
            this._currentPresentationController = new SkippableFlowStateController_1.SkippableFlowStateController(state, (state) => this.onFlowStateComplete(state), (wantSkippable) => this.setSkippable(wantSkippable));
            this._currentPresentation.start(this._currentPresentationController);
        }
        else {
            this.onFlowStateComplete(state);
        }
    }
    setSkippable(wantSkippable) {
        var _a, _b;
        const enableSkippable = SlotGame_1.SlotGame.apiPlugin.slotStates.checkState(SlotStateHandler_1.SlotState.STOPPING, SlotStateHandler_1.SlotState.SKIPPED) && wantSkippable;
        const disableSkippable = SlotGame_1.SlotGame.apiPlugin.slotStates.checkState(SlotStateHandler_1.SlotState.SKIPPABLE) && !wantSkippable;
        if (enableSkippable && disableSkippable) {
            throw new Error("You cant enable and disable skippable at the same time");
        }
        if (enableSkippable) {
            this.flowStateLogger.log(`${this._mode}, ${(_a = this._currentPresentationController) === null || _a === void 0 ? void 0 : _a.state} enable skip`);
            SlotGame_1.SlotGame.apiPlugin.events.trigger(APIEventSystem_1.APIEvent.SKIPPABLE);
        }
        if (disableSkippable) {
            this.flowStateLogger.log(`${this._mode}, ${(_b = this._currentPresentationController) === null || _b === void 0 ? void 0 : _b.state} disable skip`);
            SlotGame_1.SlotGame.apiPlugin.events.trigger(APIEventSystem_1.APIEvent.SKIP);
        }
    }
    onFlowStateComplete(state) {
        this.flowStateLogger.log(`${this._mode}, ${state} complete`);
        this._stateMap = undefined;
        this._currentPresentation = undefined;
        this._currentPresentationController = undefined;
        switch (state) {
            case FlowState_1.FlowState.IDLE:
                //Idle complete, either we wait for external bet (keypad, autoplay) or we should already have sent a "special" bet i.e ZeroBet, PickBet etc.
                break;
            case FlowState_1.FlowState.BET:
                if (SlotGame_1.SlotGame.jackpotPlugin && SlotGame_1.SlotGame.jackpotPlugin.isActive) {
                    SlotGame_1.SlotGame.jackpotPlugin.addOnSpinCompleteCallback(() => {
                        this._apiPlugin.events.trigger(APIEventSystem_1.APIEvent.STARTED);
                    });
                }
                else {
                    this._apiPlugin.events.trigger(APIEventSystem_1.APIEvent.STARTED);
                }
                break;
            case FlowState_1.FlowState.RESULT:
                this._apiPlugin.events.trigger(APIEventSystem_1.APIEvent.STOP);
                break;
            case FlowState_1.FlowState.WIN:
                this._apiPlugin.events.trigger(APIEventSystem_1.APIEvent.DONE);
                this._mode = this._model.data.nextMode;
                if (this._mode != GameMode_1.GameMode.NORMAL) {
                    this.startFlowStatePresentation(FlowState_1.FlowState.IDLE);
                }
                else {
                    this._apiPlugin.events.trigger(APIEventSystem_1.APIEvent.FINISH);
                    this._apiPlugin.slotStates.stateIsReady().then(() => {
                        this.startFlowStatePresentation(FlowState_1.FlowState.IDLE);
                    });
                }
                break;
            default:
                break;
        }
    }
    setMode(mode) {
        this._mode = mode;
    }
    skipFlowState(state) {
        if (this._stateMap == undefined) {
            throw new Error("Sequence is undefined, when skip");
        }
        if (this._currentPresentation != undefined && this._currentPresentationController != undefined) {
            this._currentPresentationController.finishCallback = () => this.onFlowStateSkipComplete(this._currentPresentationController.state);
            this._currentPresentationController.skip();
            this.flowStateLogger.log(`${this._mode}, ${state} skip`);
        }
    }
    onFlowStateSkipComplete(state) {
        this.flowStateLogger.log(`${this._mode}, ${state} skipped complete`);
        this._stateMap = undefined;
        this._currentPresentation = undefined;
        this._currentPresentationController = undefined;
        switch (state) {
            case FlowState_1.FlowState.IDLE:
                this.startFlowStatePresentation(FlowState_1.FlowState.BET);
                break;
            case FlowState_1.FlowState.BET:
                this.startFlowStatePresentation(FlowState_1.FlowState.RESULT);
                break;
            case FlowState_1.FlowState.RESULT:
                this.startFlowStatePresentation(FlowState_1.FlowState.WIN);
                break;
            case FlowState_1.FlowState.WIN:
                this.onFlowStateComplete(FlowState_1.FlowState.WIN);
                break;
            default:
                break;
        }
    }
    onApiState(state) {
        switch (state) {
            case SlotStateHandler_1.SlotState.STARTING:
                //If IDLE != complete => skip, go BET on complete
                //Else go Bet
                if (this._stateMap != undefined) {
                    this.skipFlowState(FlowState_1.FlowState.IDLE);
                }
                else {
                    this.startFlowStatePresentation(FlowState_1.FlowState.BET);
                }
                break;
            case SlotStateHandler_1.SlotState.STOPPABLE: //When Starting is complete and data has arrived
                //This only happens when BET is complete and data has arrived. So no skip can occur
                this.startFlowStatePresentation(FlowState_1.FlowState.RESULT);
                break;
            case SlotStateHandler_1.SlotState.STOPPING: //When stop trigger from Keypad or Game, After APIEvent.STOP
                //IF - RESULT != complete => skip, go WIN on commplete
                //Else Go win.
                if (this._stateMap != undefined) {
                    this.skipFlowState(FlowState_1.FlowState.RESULT);
                }
                else {
                    this.startFlowStatePresentation(FlowState_1.FlowState.WIN);
                }
                break;
            case SlotStateHandler_1.SlotState.SKIPPED: //When skip trigger from keypad (can only happen when we have asked for skippabe)
                if (this._stateMap != undefined) {
                    this.skipFlowState(FlowState_1.FlowState.WIN);
                }
                else {
                    // this.startFlowStatePresentation(GameModeSequenceState.WIN);
                }
                break;
            case SlotStateHandler_1.SlotState.GAMBLE_DONE:
                if (this._stateMap != undefined) {
                    this.skipFlowState(FlowState_1.FlowState.WIN);
                }
                else {
                    this.startFlowStatePresentation(FlowState_1.FlowState.WIN);
                }
                break;
        }
    }
    //Todo - replace with a generic version of Performance logger.
    measureTimeAtStateStart(mode, state) {
        if (mode != GameMode_1.GameMode.NORMAL) {
            return;
        }
        if (state == FlowState_1.FlowState.BET) {
            this._betStartDateTime = Date.now();
            this._normalRoundCount += 1;
        }
        if (state == FlowState_1.FlowState.IDLE) {
            if (this._normalRoundCount > 0) {
                const duration = Date.now() - this._betStartDateTime;
                this._accumulatedRoundTime += duration;
                const average = this._accumulatedRoundTime / this._normalRoundCount;
                this.roundStatsLogger.log(`%c Average:${average}  rounds:${this._normalRoundCount}`, `background-color:#0000AA; color:white;`);
            }
        }
        if (state == FlowState_1.FlowState.WIN) {
            const duration = Date.now() - this._betStartDateTime;
            if (duration < this._minimumGameRoundTimeInMs) {
                this.roundStatsLogger.log(`%c Duration BET_START -> RESULT_END:  ${duration} ms, (jurisdiction requirement: ${this._minimumGameRoundTimeInMs})`, `background-color:#AA0000; color:white;`);
            }
            else {
                this.roundStatsLogger.log(`%c Duration BET_START -> RESULT_END:  ${duration} ms, (jurisdiction requirement: ${this._minimumGameRoundTimeInMs})`, `background-color:#00AA00; color:white;`);
            }
        }
    }
}
exports.GameFlowController = GameFlowController;
//# sourceMappingURL=GameFlowController.js.map